import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CheckCircle2, FileUp, Loader2, Plus, Send } from 'lucide-react';
import { loadMailTemplates, loadMailTemplate, saveMailTemplate, sendCampaignEmailTest, loadEmailTestStatus } from '../../lib/backOfficeApi';
import { logAudit, toast } from '../../lib/buses';
import { Button } from '../../ui/Button';
import { Field, Input } from '../../ui/Form';
import { Gate } from '../../ui/Gate';
import { checkMailTemplate, fillSample, renameVariable, SAMPLE_RECIPIENT } from './mailTemplate';

// CAMPAIGN EMAILS — the HTML an email campaign sends.
//
// An email campaign's body is a named mail template in Firebase (`handlebar`); the campaign carries
// only the name. Until this panel the templates lived only in the Firebase console, so the composer
// could not show what a campaign would say. Here: see them, paste or open an .html file, check it
// against what a send actually fills, preview it, save it. Images are hosted elsewhere and referred
// to by URL — this does not upload them.

const NAME = /^[a-z0-9][a-z0-9_-]{1,79}$/;
const slugOf = (s) => String(s || '').toLowerCase().replace(/\.html?$/, '').replace(/[^a-z0-9_-]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 80);

export const MailTemplatesPanel = ({ initialName = null }) => {
  const [list, setList] = useState({ state: 'loading', templates: [], variables: [], error: '' });
  const [open, setOpen] = useState(null);          // { name, isNew }
  const [saved, setSaved] = useState(null);        // { subject, html } as stored
  const [draft, setDraft] = useState({ name: '', subject: '', html: '' });
  const [loadingOne, setLoadingOne] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const reload = (signal) => loadMailTemplates({ signal })
    .then(({ templates, variables }) => setList({ state: 'ready', templates, variables, error: '' }))
    .catch((e) => { if (!signal?.aborted) setList({ state: 'error', templates: [], variables: [], error: e.message }); });
  useEffect(() => { const ac = new AbortController(); reload(ac.signal); return () => ac.abort(); }, []);

  const openTemplate = async (name) => {
    setOpen({ name, isNew: false }); setLoadingOne(true);
    try {
      const t = await loadMailTemplate(name);
      setSaved({ subject: t.subject, html: t.html });
      setDraft({ name: t.name, subject: t.subject, html: t.html });
    } catch (e) { toast('Could not open the template', 'error', e.message); setOpen(null); }
    finally { setLoadingOne(false); }
  };
  useEffect(() => { if (initialName && list.state === 'ready' && !open) openTemplate(initialName); }, [initialName, list.state]); // eslint-disable-line react-hooks/exhaustive-deps
  const startNew = () => { setOpen({ name: '', isNew: true }); setSaved(null); setDraft({ name: '', subject: '', html: '' }); };

  const readFile = async (file) => {
    if (!file) return;
    const html = await file.text();
    const title = /<title>([^<]*)<\/title>/i.exec(html)?.[1]?.trim();
    setDraft((d) => ({
      name: d.name || (open?.isNew ? slugOf(file.name) : d.name),
      subject: d.subject || title || '',
      html,
    }));
  };

  const check = useMemo(() => checkMailTemplate(draft, list.variables), [draft, list.variables]);
  const exists = open?.isNew && list.templates.some((t) => t.name === draft.name);
  const dirty = !saved || saved.subject !== draft.subject || saved.html !== draft.html;
  const reason = !open ? null
    : !NAME.test(draft.name) ? 'Give it a name: lower-case letters, digits, - or _ — it is what a campaign refers to it by.'
    : exists ? `There is already a template named “${draft.name}” — open it from the list to change it.`
    : !draft.subject.trim() ? 'Give it a subject — it is the subject every recipient sees.'
    : !draft.html.trim() ? 'Paste the HTML, or open an .html file.'
    : !dirty ? 'Nothing has changed since it was saved.'
    : null;

  const save = async () => {
    setSaving(true);
    try {
      await saveMailTemplate(draft.name, { subject: draft.subject, html: draft.html });
      logAudit({ action: open.isNew ? 'Mail template created' : 'Mail template changed', target: draft.name, detail: draft.subject });
      toast(open.isNew ? 'Template saved' : 'Template updated', 'success', open.isNew ? `${draft.name} · a campaign can now send it` : `${draft.name} · the next send of it says this`);
      setSaved({ subject: draft.subject, html: draft.html });
      setOpen({ name: draft.name, isNew: false });
      reload();
    } catch (e) { toast('Not saved', 'error', e.message); }
    finally { setSaving(false); }
  };

  if (list.state === 'error') return <div className="mt-4 bg-white border border-[#E6E4E1] rounded-md p-4 text-[12.5px] text-[#6B7280]">{list.error}</div>;

  const sample = { ...SAMPLE_RECIPIENT, event_name: '‹event name›', event_date: '‹event date›', event_venue: '‹venue›', event_url: '#event', ticket_url: '#tickets' };

  return (
    <div className="mt-4 grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-4 items-start">
      <div className="bg-white border border-[#E6E4E1] rounded-md">
        <div className="flex items-center justify-between px-3 py-2 border-b border-[#E6E4E1]">
          <div className="text-[11px] uppercase tracking-[0.06em] font-semibold text-[#6B7280]">Templates</div>
          <Button size="sm" variant="ghost" icon={Plus} onClick={startNew}>New</Button>
        </div>
        {list.state === 'loading' ? <div className="p-3 text-[12px] text-[#9CA3AF] flex items-center gap-2"><Loader2 size={13} className="animate-spin" /> Loading…</div>
          : list.templates.length === 0 ? <div className="p-3 text-[12px] text-[#9CA3AF]">None yet.</div>
          : list.templates.map((t) => (
            <button key={t.name} onClick={() => openTemplate(t.name)}
              className={`block w-full text-left px-3 py-2 border-b border-[#F1EFEC] last:border-0 ${open?.name === t.name && !open?.isNew ? 'bg-[#1F2937] text-white' : 'hover:bg-[#FBFAF7]'}`}>
              <div className="text-[12px] font-mono truncate">{t.name}</div>
              <div className={`text-[11px] truncate ${open?.name === t.name && !open?.isNew ? 'text-white/70' : 'text-[#6B7280]'}`}>{t.subject || '— no subject —'}</div>
            </button>
          ))}
        <div className="px-3 py-2 border-t border-[#E6E4E1] text-[10.5px] text-[#9CA3AF] leading-snug">
          An email campaign sends one of these. Shared by every brand — a change applies to the next send of that template, wherever it is used.
        </div>
      </div>

      {!open ? (
        <div className="bg-white border border-[#E6E4E1] rounded-md p-6 text-[12.5px] text-[#6B7280]">
          Pick a template to see and change it, or <button className="underline" onClick={startNew}>make a new one</button> from your HTML.
        </div>
      ) : loadingOne ? (
        <div className="bg-white border border-[#E6E4E1] rounded-md p-6 text-[12px] text-[#9CA3AF] flex items-center gap-2"><Loader2 size={13} className="animate-spin" /> Opening {open.name}…</div>
      ) : (
        <div className="space-y-3">
          <div className="bg-white border border-[#E6E4E1] rounded-md p-4 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr] gap-3">
              <Field label="Name" required sub={open.isNew ? 'What a campaign refers to it by' : 'Fixed — campaigns refer to it by this'}>
                {open.isNew
                  ? <Input value={draft.name} onChange={(e) => setDraft((d) => ({ ...d, name: slugOf(e.target.value) }))} placeholder="e.g. brakaland-visa" />
                  : <div className="h-9 flex items-center font-mono text-[12.5px] text-[#1F2937]">{draft.name}</div>}
              </Field>
              <Field label="Subject" required sub="What every recipient sees — variables work here too">
                <Input value={draft.subject} onChange={(e) => setDraft((d) => ({ ...d, subject: e.target.value }))} placeholder="e.g. Your visa to B'raka Land" />
              </Field>
            </div>
            <Field label="HTML" required sub="Paste it, or open the .html file. Images must already be online — refer to them by their https:// address.">
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="secondary" icon={FileUp} onClick={() => fileRef.current?.click()}>Open .html file</Button>
                  <input ref={fileRef} type="file" accept=".html,.htm,text/html" className="hidden" onChange={(e) => { readFile(e.target.files?.[0]); e.target.value = ''; }} />
                  <span className="text-[11px] text-[#9CA3AF] tabular-nums">{draft.html.length.toLocaleString()} characters</span>
                </div>
                <textarea value={draft.html} onChange={(e) => setDraft((d) => ({ ...d, html: e.target.value }))} rows={10} spellCheck={false}
                  className="w-full bg-[#FBFAF7] border border-[#E6E4E1] rounded-md p-2.5 font-mono text-[11px] leading-[1.45] text-[#1F2937]" />
              </div>
            </Field>

            <TemplateCheck check={check} variables={list.variables}
              onRename={(from, to) => setDraft((d) => ({ ...d, subject: renameVariable(d.subject, from, to), html: renameVariable(d.html, from, to) }))} />

            <div className="flex items-center justify-end gap-2 pt-1">
              {saved && dirty && <Button variant="ghost" size="sm" onClick={() => setDraft((d) => ({ ...d, ...saved }))}>Discard changes</Button>}
              <Gate reason={reason}>
                <Button variant="primary" size="sm" onClick={save} disabled={saving}>{saving ? 'Saving…' : open.isNew ? 'Save template' : 'Save changes'}</Button>
              </Gate>
            </div>
          </div>

          <div>
            <div className="text-[10.5px] uppercase tracking-[0.06em] font-semibold text-[#6B7280] mb-1.5">Preview · as {SAMPLE_RECIPIENT.firstName}, a stand-in recipient</div>
            <MailPreview subject={fillSample(draft.subject, sample)} html={fillSample(draft.html, sample)} />
          </div>
        </div>
      )}
    </div>
  );
};

/** What the template refers to, against what a campaign send fills. */
export const TemplateCheck = ({ check, variables, onRename, needsEvent = null }) => {
  const meaning = new Map((variables || []).map((v) => [v.key, v]));
  const eventless = needsEvent === false ? check.known.filter((k) => meaning.get(k)?.needsEvent) : [];
  return (
    <div className="space-y-1.5 text-[11.5px]">
      {check.unknown.map(({ key, suggestion }) => (
        <div key={key} className="bg-[#FEF2F2] border border-[#FECACA] text-[#991B1B] rounded-md px-2.5 py-1.5 flex items-center gap-2">
          <span><code className="font-mono">{`{{${key}}}`}</code> — nothing fills this, so every recipient reads a blank there.</span>
          {suggestion && onRename && <button className="ml-auto underline whitespace-nowrap" onClick={() => onRename(key, suggestion)}>Use <code className="font-mono">{`{{${suggestion}}}`}</code></button>}
        </div>
      ))}
      {eventless.map((key) => (
        <div key={key} className="bg-[#FEF7E6] border border-[#FDE68A] text-[#8A5A12] rounded-md px-2.5 py-1.5">
          <code className="font-mono">{`{{${key}}}`}</code> is filled from the campaign's event — this campaign has none, so it reads blank.
        </div>
      ))}
      {check.localImages.map((src) => (
        <div key={src} className="bg-[#FEF7E6] border border-[#FDE68A] text-[#8A5A12] rounded-md px-2.5 py-1.5">
          Image <code className="font-mono">{src}</code> is a file on a computer, not an address — it will not load in anyone's inbox. Put it online and use its https:// address.
        </div>
      ))}
      <div className="text-[#6B7280] leading-relaxed">
        {check.known.length > 0 && <>Filled on every send: {check.known.map((k) => <code key={k} className="font-mono text-[#1F2937] mr-1.5" title={meaning.get(k)?.means}>{`{{${k}}}`}</code>)}<br /></>}
        Available: {(variables || []).map((v) => <code key={v.key} className="font-mono mr-1.5" title={v.means}>{`{{${v.key}}}`}</code>)}
      </div>
    </div>
  );
};

/** The email as an inbox draws it: its own document, sandboxed, at the width it was designed for. */
export const MailPreview = ({ subject, html }) => (
  <div className="bg-white border border-[#E6E4E1] rounded-md overflow-hidden">
    <div className="px-3 py-2 border-b border-[#E6E4E1] text-[12.5px] font-semibold text-[#1F2937]">{subject || <span className="text-[#9CA3AF] font-normal">No subject</span>}</div>
    <iframe title="Email preview" sandbox="" srcDoc={html} className="w-full h-[640px] block bg-white" />
  </div>
);

/**
 * Send the campaign's email to a few addresses first — the same template, sender and cc the send
 * uses, with the sender's own name in it — then watch the mail service say whether each landed.
 */
export const EmailTest = ({ templateName, fromName, eventId }) => {
  const [to, setTo] = useState('');
  const [state, setState] = useState({ status: 'idle', results: [] });
  const watch = (ids, addresses) => {
    let tries = 0;
    const tick = async () => {
      tries += 1;
      const results = await Promise.all(ids.map((id, i) => loadEmailTestStatus(id).catch((e) => ({ to: addresses[i], state: 'UNKNOWN', error: e.message }))));
      setState((s) => ({ ...s, results }));
      if (results.some((r) => r.state === 'PENDING' || r.state === 'PROCESSING') && tries < 20) setTimeout(tick, 3000);
    };
    setTimeout(tick, 2000);
  };
  const run = async () => {
    setState({ status: 'sending', results: [] });
    try {
      const r = await sendCampaignEmailTest({ templateName, to, fromName, eventId });
      setState({ status: 'sent', from: r.from, results: r.to.map((a) => ({ to: a, state: 'PENDING' })) });
      if (r.mailIds?.length) watch(r.mailIds, r.to);
    } catch (e) { setState({ status: 'error', message: e.message, results: [] }); }
  };
  const word = { PENDING: 'Queued', PROCESSING: 'Sending', SUCCESS: 'Accepted by the mail server', ERROR: 'Failed', UNKNOWN: 'Unknown' };
  return (
    <div className="bg-white border border-[#E6E4E1] rounded-md p-3 space-y-2">
      <div className="text-[12px] font-semibold text-[#1F2937]">Send a test first</div>
      <div className="text-[10.5px] text-[#6B7280]">The same email, sender and cc the campaign sends, with your name in it — to up to 5 addresses, before anyone in the audience gets anything.</div>
      <div className="flex gap-2">
        <input value={to} onChange={(e) => setTo(e.target.value)} placeholder="you@example.com, a colleague@example.com" className="flex-1 h-8 px-2.5 text-[12px] border border-[#E6E4E1] rounded-md bg-white focus:outline-none focus:border-[#1F2937]" />
        <Gate reason={!templateName ? 'Pick the email first.' : !to.trim() ? 'Enter the address to send the test to.' : null}>
          <button onClick={run} disabled={state.status === 'sending'} className="h-8 px-3 text-[12px] font-medium rounded-md bg-[#1F2937] hover:bg-[#111827] disabled:bg-[#9CA3AF] text-white inline-flex items-center gap-1.5">
            {state.status === 'sending' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}Send test
          </button>
        </Gate>
      </div>
      {state.status === 'sent' && state.from && <div className="text-[11px] text-[#6B7280]">From {state.from.from} &lt;{state.from.mailbox || state.from.senderdomain}&gt;</div>}
      {state.results.map((r) => (
        <div key={r.to} className={`text-[11px] inline-flex items-center gap-1.5 mr-3 ${r.state === 'SUCCESS' ? 'text-[#166534]' : r.state === 'ERROR' ? 'text-[#9B1F1F]' : 'text-[#6B7280]'}`}>
          {r.state === 'SUCCESS' ? <CheckCircle2 className="w-3.5 h-3.5" /> : r.state === 'ERROR' ? null : <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          {r.to} · {word[r.state] || r.state}{r.error ? ` — ${r.error}` : ''}
        </div>
      ))}
      {state.status === 'error' && <div className="text-[11px] text-[#9B1F1F]">{state.message}</div>}
    </div>
  );
};
